import React, { useEffect, useState, useRef } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { ArrowDownIcon, FileTextIcon, LinkedinIcon, SparklesIcon, TrendingUpIcon, UsersIcon, Calendar } from 'lucide-react';
import { ScheduleCallButton } from './ScheduleCallButton';
const Hero = ({
  language,
  toggleLanguage
}) => {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  const heroRef = useRef(null);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    const handleMouseMove = e => {
      if (isMobile) return;
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);
  const scrollToNext = () => {
    document.getElementById('logos')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const translations = {
    en: {
      greeting: "Hi, I'm",
      tagline: 'Finance Student • Asset Management • Investment Banking',
      bio: 'Northeastern University finance student with experience across asset management at State Street ($5T+ AUM), investment banking at KCC Capital, FX trading at OCB, and real estate PE at Cavalina. Building expertise in financial markets and investment strategies.',
      impact: 'Community Impact',
      impactNumber: '100K+',
      impactLabel: 'Lives Impacted',
      leadership: 'Leadership',
      leadershipNumber: '40+',
      leadershipLabel: 'Students Educated',
      experience: 'Experience',
      experienceNumber: '6+',
      experienceLabel: 'Finance Roles',
      viewResume: 'View Resume',
      connect: 'Connect',
      scrollDown: 'Explore My Work',
      typewriterTexts: ['Asset Management Analyst', 2000, 'Investment Banking Analyst', 2000, 'FX Trading Analyst', 2000, 'Real Estate PE Intern', 2000]
    },
    vi: {
      greeting: 'Xin chào, tôi là',
      tagline: 'Sinh viên Tài chính • Quản lý Tài sản • Ngân hàng Đầu tư',
      bio: 'Sinh viên tài chính tại Đại học Northeastern với kinh nghiệm về quản lý tài sản tại State Street ($5T+ AUM), ngân hàng đầu tư tại KCC Capital, giao dịch FX tại OCB, và quỹ đầu tư bất động sản tại Cavalina. Xây dựng chuyên môn trong thị trường tài chính và chiến lược đầu tư.',
      impact: 'Tác Động Cộng Đồng',
      impactNumber: '100K+',
      impactLabel: 'Cuộc Sống Tác Động',
      leadership: 'Lãnh Đạo',
      leadershipNumber: '40+',
      leadershipLabel: 'Sinh Viên Đào Tạo',
      experience: 'Kinh Nghiệm',
      experienceNumber: '6+',
      experienceLabel: 'Vai Trò Tài Chính',
      viewResume: 'Xem Sơ Yếu Lý Lịch',
      connect: 'Kết Nối',
      scrollDown: 'Khám Phá Công Việc Của Tôi',
      typewriterTexts: ['Chuyên Viên Quản Lý Tài Sản', 2000, 'Chuyên Viên Ngân Hàng Đầu Tư', 2000, 'Chuyên Viên Giao Dịch FX', 2000, 'Thực Tập Sinh Quỹ BĐS', 2000]
    }
  };
  const t = translations[language];
  const stats = [{
    icon: UsersIcon,
    number: t.impactNumber,
    label: t.impactLabel,
    color: 'from-accent-cyan to-blue-500',
    delay: '0.3s'
  }, {
    icon: SparklesIcon,
    number: t.leadershipNumber,
    label: t.leadershipLabel,
    color: 'from-accent-violet to-purple-500',
    delay: '0.5s'
  }, {
    icon: TrendingUpIcon,
    number: t.experienceNumber,
    label: t.experienceLabel,
    color: 'from-accent-amber to-orange-500',
    delay: '0.7s'
  }];
  return <section ref={heroRef} id="hero" className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-secondary to-background" data-id="element-796">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-797">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-accent-cyan/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-798" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-accent-violet/10 rounded-full blur-3xl animate-pulse-slow" style={{
        animationDelay: '1s'
      }} data-id="element-799" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-amber/5 rounded-full blur-3xl animate-pulse-slow" style={{
        animationDelay: '2s'
      }} data-id="element-800" />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.03)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" data-id="element-801" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20" data-id="element-802">
        <div className="max-w-5xl mx-auto" data-id="element-803">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-804">
            {/* Left column - Text content */}
            <div className="space-y-6 text-center lg:text-left" data-id="element-805">
              <div className="opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]" style={{
              animationDelay: '0.1s'
            }} data-id="element-806">
                <p className="text-accent-cyan text-sm md:text-base font-medium tracking-wide uppercase mb-2" data-id="element-807">
                  {t.greeting}
                </p>
                <h1 className="text-5xl sm:text-6xl md:text-7xl font-display font-bold tracking-tight" data-id="element-808">
                  <span className="gradient-text" data-id="element-809">Trung Nguyen</span>
                </h1>
              </div>

              <div className="h-16 flex items-center justify-center lg:justify-start opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]" style={{
              animationDelay: '0.3s'
            }} data-id="element-810">
                <TypeAnimation sequence={t.typewriterTexts} wrapper="span" speed={50} className="text-xl sm:text-2xl md:text-3xl text-muted font-medium" repeat={Infinity} data-id="element-811" />
              </div>

              <p className="text-muted text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]" style={{
              animationDelay: '0.5s'
            }} data-id="element-812">
                {t.bio}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start opacity-0 animate-[fadeInUp_0.6s_ease-out_forwards]" style={{
              animationDelay: '0.7s'
            }} data-id="element-813">
                <ScheduleCallButton language={language} variant="primary" data-id="element-814" />

                <a href="https://www.linkedin.com/in/realtrungnguyen" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-secondary/50 backdrop-blur-sm border border-accent-cyan/30 rounded-full font-semibold text-primary hover:bg-secondary hover:border-accent-cyan transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2" data-id="element-815">
                  <LinkedinIcon size={20} data-id="element-816" />
                  {t.connect}
                </a>
              </div>
            </div>

            {/* Right column - Stats cards */}
            <div className="space-y-4" data-id="element-817">
              {stats.map((stat, index) => {
              const Icon = stat.icon;
              return <div key={index} className="group relative bg-secondary/50 backdrop-blur-sm border border-accent-cyan/10 rounded-2xl p-6 hover:border-accent-cyan/30 transition-all duration-300 hover:scale-105 opacity-0 animate-[fadeInRight_0.6s_ease-out_forwards]" style={{
                animationDelay: stat.delay,
                transform: isMobile ? 'none' : `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`,
                transition: 'transform 0.3s ease-out'
              }} data-id="element-818">
                    <div className="flex items-center gap-4" data-id="element-819">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`} data-id="element-820">
                        <Icon size={28} className="text-white" data-id="element-821" />
                      </div>
                      <div data-id="element-822">
                        <div className="text-3xl font-bold gradient-text mb-1" data-id="element-823">
                          {stat.number}
                        </div>
                        <div className="text-muted text-sm" data-id="element-824">{stat.label}</div>
                      </div>
                    </div>
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl`} data-id="element-825" />
                  </div>;
            })}
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-0 animate-[fadeIn_0.6s_ease-out_forwards]" style={{
          animationDelay: '1s'
        }} data-id="element-826">
            <button onClick={scrollToNext} className="group flex flex-col items-center gap-2 text-muted hover:text-accent-cyan transition-colors duration-300" data-id="element-827">
              <span className="text-sm font-medium" data-id="element-828">{t.scrollDown}</span>
              <div className="w-6 h-10 border-2 border-current rounded-full flex items-start justify-center p-2 group-hover:border-accent-cyan transition-colors" data-id="element-829">
                <div className="w-1.5 h-1.5 bg-current rounded-full animate-scroll" data-id="element-830" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>;
};
export default Hero;